import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
} from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import HomeIcon from "@mui/icons-material/Home";
import SsidChartIcon from "@mui/icons-material/SsidChart";
import RuleFolderIcon from "@mui/icons-material/RuleFolder";
import ApiIcon from "@mui/icons-material/Api";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import ChecklistRtlRoundedIcon from "@mui/icons-material/ChecklistRtlRounded";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TaxCalculator from "../components/tools/TaxCalculator";
import MoneyMap from "../components/tools/MoneyMap";
import MindMap from "../components/tools/MindMap";
import Forecasting from "../components/tools/Forecasting";
import ShoppingGroceries from "../components/tools/ShoppingGroceries";
import GroceryMeals from "../components/tools/GroceryMeals";
import HousingForecast from "../components/tools/HousingForecast";
import MortgageCalculator from "../components/tools/MortgageCalculator";
import Charts from "../components/tools/Charts";
import Rules from "../components/tools/Rules";
import ApiDocs from "../components/tools/ApiDocs";
import SpouseValueCalculator from "../components/tools/SpouseValueCalculator";
import HouseholdTasksTool from "../components/tools/HouseholdTasksTool";
import ExpensesComparisonPage from "./ExpensesComparisonPage";

interface Tool {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  component?: React.ComponentType;
  externalUrl?: string;
  tags: string[];
  fullWidth?: boolean;
}

const tools: Tool[] = [
  {
    id: "money-map",
    name: "Money Map",
    description:
      "A vertical focus chart that sorts everything you're working on by the kind of money it makes: consistent, quick, and big.",
    icon: <AccountBalanceIcon sx={{ fontSize: 48 }} />,
    component: MoneyMap,
    tags: ["Focus", "Planning", "Income", "Strategy"],
    fullWidth: true,
  },
  {
    id: "tax-calculator",
    name: "Tax Calculator",
    description:
      "Calculate your take-home pay after federal and New York state taxes based on hourly or salary income.",
    icon: <CalculateIcon sx={{ fontSize: 48 }} />,
    component: TaxCalculator,
    tags: ["Income", "Taxes", "Paycheck"],
  },
  {
    id: "mortgage-calculator",
    name: "Mortgage Calculator",
    description:
      "Estimate mortgage payments with credit-score-based rates, Bend, OR property tax, homeowners insurance, and tax impacts. Shows all math.",
    icon: <HomeIcon sx={{ fontSize: 48 }} />,
    component: MortgageCalculator,
    tags: ["Housing", "Mortgage", "Taxes", "Calculator"],
    fullWidth: true,
  },
  {
    id: "housing-forecast",
    name: "Housing Forecast",
    description:
      "Model housing scenarios using real estate sale proceeds, tax estimates, and your take-home pay.",
    icon: <TrendingUpIcon sx={{ fontSize: 48 }} />,
    component: HousingForecast,
    tags: ["Housing", "Real Estate", "Taxes", "Projection"],
    fullWidth: true,
  },
  {
    id: "debt-payoff-forecasting",
    name: "Debt Payoff Forecasting",
    description:
      "See your total debts, income, and expenses at a glance. Calculate how long it takes to pay off debt at different hourly wages.",
    icon: <TrendingUpIcon sx={{ fontSize: 48 }} />,
    component: Forecasting,
    tags: ["Debts", "Income", "Expenses", "Calculator"],
    fullWidth: true,
  },
  {
    id: "shopping-groceries",
    name: "Lists",
    description:
      "Create shopping lists and track grocery items. Lists sync as dynamic expenses that update automatically.",
    icon: <ShoppingCartIcon sx={{ fontSize: 48 }} />,
    component: ShoppingGroceries,
    tags: ["Shopping", "Groceries", "Expenses", "Lists"],
    fullWidth: true,
  },
  {
    id: "grocery-meals",
    name: "Grocery & Meals",
    description:
      "Plan meals on a calendar, generate ingredients with AI, and keep a price-aware ingredient ledger for iOS and web.",
    icon: <RestaurantMenuIcon sx={{ fontSize: 48 }} />,
    component: GroceryMeals,
    tags: ["Meals", "Groceries", "AI", "Planning"],
    fullWidth: true,
  },
  {
    id: "family-meal-planner",
    name: "Family Meal Planner",
    description:
      "Open the standalone Family Meal Planner for calendar-based meal planning.",
    icon: <RestaurantMenuIcon sx={{ fontSize: 48 }} />,
    externalUrl: "https://meal-planner-egu.pages.dev",
    tags: ["Meals", "Planning", "Family"],
  },
  {
    id: "mind-map",
    name: "Mind Map",
    description:
      "Create visual mind maps for brainstorming, planning, and organizing ideas with hierarchical diagrams.",
    icon: <AccountTreeIcon sx={{ fontSize: 48 }} />,
    component: MindMap,
    tags: ["Planning", "Visualization", "Brainstorming"],
    fullWidth: true,
  },
  {
    id: "rules",
    name: "Rules",
    description:
      "Create and edit Smart AI transaction categorization rules used across Transactions.",
    icon: <RuleFolderIcon sx={{ fontSize: 48 }} />,
    component: Rules,
    tags: ["Transactions", "AI", "Categories", "Rules"],
    fullWidth: true,
  },
  {
    id: "charts",
    name: "Charts",
    description:
      "Visual flow charts for income by vendor and expenses by vendor.",
    icon: <SsidChartIcon sx={{ fontSize: 48 }} />,
    component: Charts,
    tags: ["Income", "Expenses", "Vendor", "Charts"],
    fullWidth: true,
  },
  {
    id: "api",
    name: "API",
    description:
      "Learn how to use the Focus Finance API: authentication, endpoints, and limitations.",
    icon: <ApiIcon sx={{ fontSize: 48 }} />,
    component: ApiDocs,
    tags: ["API", "Integrations", "Automation"],
    fullWidth: true,
  },
  {
    id: "household-tasks",
    name: "Household Tasks",
    description:
      "Manage the chores and school task sheet with editable table filters, derived totals, and charts.",
    icon: <ChecklistRtlRoundedIcon sx={{ fontSize: 48 }} />,
    component: HouseholdTasksTool,
    tags: ["Household", "Tasks", "Charts", "Planning"],
    fullWidth: true,
  },
  {
    id: "spouse-value-calculator",
    name: "Spouse Value Calculator",
    description:
      "Estimate the replacement-cost value of household roles using editable rates and schedule settings.",
    icon: <VolunteerActivismIcon sx={{ fontSize: 48 }} />,
    component: SpouseValueCalculator,
    tags: ["Household", "Calculator", "Schedule"],
    fullWidth: true,
  },
  {
    id: "expenses-comparison",
    name: "Expenses Comparison",
    description:
      "Compare your expected expenses against actual data. Track pricing, recurring status, and quickly add missing items.",
    icon: <CompareArrowsIcon sx={{ fontSize: 48 }} />,
    component: ExpensesComparisonPage,
    tags: ["Expenses", "Comparison", "Tracking"],
    fullWidth: true,
  },
];

const ToolsPage: React.FC = () => {
  const { toolId } = useParams<{ toolId?: string }>();
  const navigate = useNavigate();

  const selectedTool = toolId ? tools.find((t) => t.id === toolId) : null;

  const handleToolSelect = (tool: Tool) => {
    if (tool.externalUrl) {
      console.debug("[ToolsPage] Opening external tool", {
        toolId: tool.id,
        url: tool.externalUrl,
      });
      window.open(tool.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }

    navigate(`/tools/${tool.id}`);
  };

  return (
    <Container
      maxWidth={selectedTool?.fullWidth ? false : "lg"}
      sx={{ py: 3, px: selectedTool?.fullWidth ? 3 : undefined }}
    >
      {!selectedTool ? (
        // Tools List View
        <Box>
          <Grid container spacing={3}>
            {tools.map((tool) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={tool.id}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleToolSelect(tool)}
                    sx={{ height: "100%", p: 2 }}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          color: "primary.main",
                          mb: 2,
                          p: 2,
                          borderRadius: "50%",
                          bgcolor: "action.hover",
                        }}
                      >
                        {tool.icon}
                      </Box>
                      <Typography variant="h6" gutterBottom>
                        {tool.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, flexGrow: 1 }}
                      >
                        {tool.description}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          flexWrap: "wrap",
                          justifyContent: "center",
                        }}
                      >
                        {tool.tags.map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>

          {tools.length === 1 && (
            <Paper sx={{ p: 3, mt: 4, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                More tools coming soon! Check back for additional calculators
                and utilities.
              </Typography>
            </Paper>
          )}
        </Box>
      ) : (
        // Selected Tool View
        <Box>
          {selectedTool.component
            ? React.createElement(selectedTool.component)
            : null}
        </Box>
      )}
    </Container>
  );
};

export default ToolsPage;
